import { Link, useParams } from "react-router-dom";
import { newsCategories } from "@/mocks/news";
import { getNewsById, getRelatedNews } from "@/utils/data";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import ShareButton from "@/components/feature/ShareButton";

export default function NewsDetailPage() {
  const { id } = useParams();
  const item = getNewsById(id);
  const categoryInfo = newsCategories.find((c) => c.key === item?.category);

  usePageMeta({
    title: item ? `${item.title} - 黄色在线` : "资讯详情 - 黄色在线",
    description: item ? item.summary : "影视资讯详情页。",
    keywords: item
      ? `${item.title},黄色在线,影视资讯,${categoryInfo ? categoryInfo.name : ""}`
      : "黄色在线,影视资讯",
  });

  if (!item) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <div className="mx-auto flex max-w-lg flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-10 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-300" aria-hidden="true" />
          <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该资讯</h1>
          <p className="text-sm text-foreground-500">内容可能已被移除，去看看其他资讯吧。</p>
          <Link
            to="/news/industry"
            className="mt-2 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
          >
            <i className="ri-newspaper-line" aria-hidden="true" />
            浏览影视资讯
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedNews(item, 4);

  return (
    <div className="w-full">
      <section className="w-full bg-primary-900 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb
          light
          items={[
            { label: "影视资讯", to: `/news/${item.category}` },
            { label: item.title },
          ]}
        />
        <h1 className="mt-4 max-w-4xl font-heading text-2xl font-extrabold leading-snug text-background-50 md:text-3xl">
          {item.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-300">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-user-line" aria-hidden="true" />
            {item.author}
          </span>
          <time className="inline-flex items-center gap-1.5">
            <i className="ri-calendar-line" aria-hidden="true" />
            {item.publishDate}
          </time>
          {categoryInfo ? (
            <Link
              to={`/news/${item.category}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/20 px-3 py-0.5 text-xs font-medium text-accent-300 transition-colors hover:bg-accent-500/30"
            >
              <i className="ri-price-tag-3-line" aria-hidden="true" />
              {categoryInfo.name}
            </Link>
          ) : null}
        </div>
      </section>

      <section className="w-full px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <div className="w-full overflow-hidden rounded-lg bg-secondary-200" style={{ aspectRatio: "16 / 9" }}>
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 黄色在线`}
                className="h-full w-full object-cover"
              />
            </div>

            <div className="mt-6 flex flex-col gap-4 text-sm leading-8 text-foreground-700 md:text-base">
              {item.content.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
              <p className="text-sm text-foreground-400">本文由 黄色在线 编辑整理，仅供演示参考。</p>
              <ShareButton />
            </div>
          </article>

          <aside className="lg:col-span-4">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
              相关资讯
            </h2>
            <div className="mt-4 flex flex-col gap-3">
              {related.map((n) => (
                <NewsCard key={n.id} item={n} />
              ))}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}